import type { MapCustomMapStarterSheetBodyProps } from "../../contracts/map-custom-maps";
import { cn } from "../../lib/utils";
import { Input } from "../../internal/primitives/input";
import { Label } from "../../internal/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import { Separator } from "../../internal/primitives/separator";
import { Palette, Loader2, ExternalLink, Layers } from "lucide-react";

/**
 * MapCustomMapStarterSheetBody provides a guided configuration flow for creating a new custom map.
 * It focuses on selecting a published style preset that includes one or more data sources.
 */
export function MapCustomMapStarterSheetBody({
  title,
  description,
  mapName,
  mapNamePlaceholder = "My Custom Map",
  styleValue,
  styleOptions,
  stylePlaceholder = "Select a starting preset...",
  styleEmptyMessage = "No published presets available",
  summaryItems = [],
  helperText,
  errorMessage,
  submitting,
  onMapNameChange,
  onStyleChange,
  onCancel,
  onSubmit,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}: MapCustomMapStarterSheetBodyProps) {
  const selectedStyle = styleOptions.find((o) => o.value === styleValue);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="space-y-1.5">
        <h2 className="text-lg font-black tracking-tight text-foreground">
          {title}
        </h2>
        {description && (
          <p className="text-xs leading-relaxed text-muted-foreground/80">
            {description}
          </p>
        )}
      </div>

      <div className="space-y-5">
        {/* Map Name Field */}
        <div className="space-y-2">
          <Label
            htmlFor="map-name"
            className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70"
          >
            Map Name
          </Label>
          <Input
            id="map-name"
            value={mapName}
            onChange={(e) => onMapNameChange(e.target.value)}
            placeholder={mapNamePlaceholder}
            className="h-10 text-sm font-medium"
          />
        </div>

        {/* Style Preset Selection */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5 text-muted-foreground/60" />
            <Label
              htmlFor="style-select"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70"
            >
              Style Preset
            </Label>
          </div>
          <Select
            value={styleValue}
            onValueChange={onStyleChange}
            disabled={styleOptions.length === 0}
          >
            <SelectTrigger id="style-select" className="h-10 text-sm">
              <SelectValue
                placeholder={
                  styleOptions.length === 0
                    ? styleEmptyMessage
                    : stylePlaceholder
                }
              />
            </SelectTrigger>
            <SelectContent>
              {styleOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  <div className="flex flex-col py-1">
                    <span className="font-medium">{option.label}</span>
                    <div className="flex items-center gap-2 text-[10px] opacity-60">
                      {option.metaLabel && <span>{option.metaLabel}</span>}
                      {option.metaLabel && option.sourceSummary && (
                        <span className="opacity-40">•</span>
                      )}
                      {option.sourceSummary && (
                        <span className="truncate">{option.sourceSummary}</span>
                      )}
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Selected Preset Source Summary */}
        {selectedStyle && (
          <div className="rounded-md border border-border/50 bg-muted/20 px-3 py-2.5">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
              <Layers className="h-3 w-3" />
              Included Sources
            </div>
            <p className="text-xs font-medium text-foreground/90">
              {selectedStyle.sourceSummary || "No sources defined for this preset"}
            </p>
            {selectedStyle.description && (
              <p className="mt-1 text-[10px] leading-normal text-muted-foreground/70">
                {selectedStyle.description}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Contextual Summary & Feedback */}
      {(summaryItems.length > 0 || helperText || errorMessage) && (
        <div className="space-y-4 rounded-lg bg-muted/30 p-4 ring-1 ring-border/40">
          {summaryItems.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {summaryItems.map((item) => (
                <div key={item.id} className="space-y-0.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
                    {item.label}
                  </div>
                  <div className="text-xs font-semibold text-foreground">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {summaryItems.length > 0 && (helperText || errorMessage) && (
            <Separator className="bg-border/40" />
          )}

          {errorMessage ? (
            <p className="text-[10px] font-medium leading-normal text-destructive">
              {errorMessage}
            </p>
          ) : helperText ? (
            <p className="text-[10px] leading-normal text-muted-foreground/70">
              {helperText}
            </p>
          ) : null}
        </div>
      )}

      {/* Action Affordances */}
      <div className="mt-2 flex flex-col gap-3">
        <button
          onClick={onSubmit}
          disabled={submitting || !mapName || !styleValue}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-xs font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Confirm Selections
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="inline-flex h-9 flex-1 items-center justify-center rounded-md border border-input bg-background px-4 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            Cancel
          </button>

          {secondaryActionLabel && onSecondaryAction && (
            <button
              onClick={onSecondaryAction}
              disabled={submitting}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md border border-transparent px-4 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {secondaryActionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
